const urls = ['/audio/codex-tech-sequence.mp3', '/audio/codex-orbit.mp3'];

// Short loops are decoded once and scheduled on the audio clock, independently
// of scroll rendering, media-element buffering, and repeated touch/click events.
export async function decodeMusicTrack(context, bytes, fetchAudio, url) {
  async function decode(data) {
    let timer;
    try {
      return await Promise.race([
        context.decodeAudioData(data),
        new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('Audio decode timed out')), 5000); }),
      ]);
    } finally { clearTimeout(timer); }
  }
  try { return await decode(bytes.slice(0)); }
  catch { return decode(await fetchAudio(url.replace(/\.mp3$/, '.wav'))); }
}

export function createMusicPlayback({createContext, fetchAudio, onPlaying, isHidden}) {
  let ctx, pending, epoch, disposed = false, enabled = false;
  let space = false, nearSpace = false, mixedScene = null;
  const downloads = [], loading = [], tracks = [];
  function warm(next = space) {
    const index = Number(next);
    if (!downloads[index]) downloads[index] = Promise.resolve().then(() => fetchAudio(urls[index])).catch(error => {
      downloads[index] = null;
      throw error;
    });
    return downloads[index];
  }
  function prepareTrack(index) {
    if (tracks[index]) return Promise.resolve(tracks[index]);
    if (!loading[index]) loading[index] = warm(Boolean(index))
      .then(bytes => decodeMusicTrack(ctx, bytes, fetchAudio, urls[index]))
      .then(buffer => {
        downloads[index] = null;
        if (disposed) return;
        const source = ctx.createBufferSource(), gain = ctx.createGain();
        source.buffer = buffer; source.loop = true; gain.gain.value = 0;
        source.connect(gain).connect(ctx.destination);
        const start = ctx.currentTime + .05;
        epoch ??= start;
        // A later track joins the existing audio timeline instead of restarting
        // the current loop or making scroll/tap events reset its position.
        const offset = buffer.duration ? Math.max(0, start - epoch) % buffer.duration : 0;
        source.start(start, offset);
        return tracks[index] = {source, gain};
      }).catch(error => {loading[index] = null; throw error;});
    return loading[index];
  }
  function prepareNearbyScene() {
    if (!nearSpace || !enabled || ctx?.state !== 'running') return;
    return prepareTrack(1).then(() => {
      if (!disposed && enabled && space && mixedScene !== space) mix(mixedScene !== null);
    }).catch(() => {});
  }
  function mix(sceneChange) {
    // Keep the existing music audible until the destination is decoded. This
    // also covers a direct navigation to Moon before its prefetch completes.
    if (!ctx || !tracks[Number(space)]) return;
    const now = ctx.currentTime;
    tracks.forEach(({gain}, index) => {
      const parameter = gain.gain;
      if (typeof parameter.cancelAndHoldAtTime === 'function') parameter.cancelAndHoldAtTime(now);
      else {
        const value = parameter.value;
        parameter.cancelScheduledValues(now);
        parameter.setValueAtTime(value, now);
      }
      if (sceneChange) {
        parameter.linearRampToValueAtTime(0, now + 1.4);
        if (index === Number(space)) parameter.linearRampToValueAtTime(.22, now + 3.4);
      } else parameter.linearRampToValueAtTime(index === Number(space) ? .22 : 0, now + 2);
    });
    mixedScene = space;
  }
  async function play() {
    if (disposed) return;
    enabled = true;
    if (!ctx) {
      ctx = createContext();
      ctx.onstatechange = () => {
        if (disposed) return;
        onPlaying(enabled && ctx.state === 'running' && tracks.some(Boolean));
        // Recover actual Safari interruptions without restarting loops or fades.
        if (ctx.state === 'interrupted' && enabled && !isHidden()) void play().catch(() => {});
      };
    }
    // Always issue resume in the trusted gesture, even if an earlier autoplay
    // attempt is still pending. Share the rest of startup between both events.
    const resumed = ctx.state === 'running' ? Promise.resolve() : ctx.resume();
    if (pending) { await resumed; return pending; }
    pending = (async () => {
      await resumed;
      if (!enabled || isHidden()) { stop(); return; }
      await prepareTrack(Number(space));
      if (disposed) return;
      if (!enabled || isHidden()) { stop(); return; }
      if (mixedScene !== space) mix(mixedScene !== null);
      onPlaying(ctx.state === 'running');
      prepareNearbyScene();
    })();
    try { await pending; }
    catch (error) { if (!disposed) onPlaying(false); throw error; }
    finally { pending = null; }
  }
  function stop() {
    enabled = false;
    if (ctx && ctx.state !== 'closed') void ctx.suspend().catch(() => {});
    if (!disposed) onPlaying(false);
  }
  return {
    warm, play, stop,
    isRunning: () => enabled && ctx?.state === 'running',
    setScene(next) {
      if (space === next) return;
      space = next;
      if (!enabled || !ctx) return;
      if (mixedScene === space) return;
      if (tracks[Number(space)]) mix(mixedScene !== null);
      else return prepareTrack(Number(space)).then(() => {
        if (!disposed && enabled && mixedScene !== space) mix(mixedScene !== null);
      }).catch(() => {});
    },
    prepareScene(next) {
      nearSpace = next;
      return prepareNearbyScene();
    },
    dispose() {
      disposed = true; enabled = false;
      if (ctx) ctx.onstatechange = null;
      tracks.forEach(({source, gain}) => {source.stop(); source.disconnect(); gain.disconnect();});
      tracks.length = 0;
      if (ctx) void ctx.close().catch(() => {});
    },
  };
}
